import Parse from 'parse';
import { InjectionToken, Provider } from '@angular/core';
import { CaseTypeRepository, MethodTypeRepository } from '@telespot/domain';
import { ParseCaseTypeRepository, ParseMethodTypeRepository } from '@telespot/infrastructure/parse';
import { RetrieveCaseTypeQuery } from '@protocols/use-cases';

import { CaseTypeEditorEffects, RETRIEVE_CASE_TYPE_QUERY } from '../../../effects/case-type-editor.effects';
import { CaseTypeEditorState } from '../../../state/case-type-editor.state';

import { CaseTypeEditorFacade } from './case-type-editor.facade';

const EDITOR_CASE_TYPE_REPOSITORY = new InjectionToken<CaseTypeRepository>('EDITOR_CASE_TYPE_REPOSITORY');
const EDITOR_METHOD_TYPE_REPOSITORY = new InjectionToken<MethodTypeRepository>('EDITOR_METHOD_TYPE_REPOSITORY');

/**
 * Providers for ONE case-type editing session: the editor state, the retrieve
 * query over the session's repositories (case-type for the row/fields,
 * method-type for the assigned-list names) and the effects + facade.
 *
 * The shared staging ledgers the facade/effects also inject
 * (`StagedCaseDataState`, `StagedMethodTypesState`, `AssignedMethodTypesState`,
 * plus the facade's error-surfacing sources — `LabelState`, `LabelConfigState`,
 * `StagedPipelinesState`, `StagedResourcesState` and the shared
 * `LabelConsolidationFailureEffects`) are deliberately NOT here — they are
 * owned by the OUTERMOST consumer (the case-type editor page), exactly as
 * `methodTypeEditorProviders` excludes `AssignedResourcesState`. Stories
 * provide them directly.
 */
export const caseTypeEditorProviders: Provider[] = [
  CaseTypeEditorState,
  {
    provide: EDITOR_CASE_TYPE_REPOSITORY,
    useFactory: () => new ParseCaseTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: EDITOR_METHOD_TYPE_REPOSITORY,
    useFactory: () => new ParseMethodTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: RETRIEVE_CASE_TYPE_QUERY,
    useFactory: (caseTypeRepository: CaseTypeRepository, methodTypeRepository: MethodTypeRepository) =>
      new RetrieveCaseTypeQuery(caseTypeRepository, methodTypeRepository),
    deps: [EDITOR_CASE_TYPE_REPOSITORY, EDITOR_METHOD_TYPE_REPOSITORY],
  },
  CaseTypeEditorEffects,
  CaseTypeEditorFacade,
];
